import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Badge, Button, Modal, Stack, Title } from '@mantine/core';
import Callout from '@ui/components/Callout/Callout';
import Text from '@ui/components/Text/Text';

import useDelayedOpening from 'src/new_arch/hooks/useDelayedOpening';
import Loader from '../../../../../components/Loader/Loader';
import PriceLine from '../../../../../components/subscription/PriceLine/PriceLine';
import AddOnsSwitch from '../../../../../components/AddOnsSwitch/AddOnsSwitch';

import './AddOnsModal.scss';
import { PRICING_PLANS_AVAILABLE } from '../../../../../config/global';
import AddOnModalPresenter from './presenter/AddOnsModalPresenter';

export type AddOnsModalProps = {
	presenter: AddOnModalPresenter
};

const MODAL_STYLES = {
	header: {
		borderBottom: '1px solid var(--mantine-color-gray-2)'
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: '20px 28px 28px 28px'
	}
} as const;

export const AddOnsModal = ( {
	presenter
}: AddOnsModalProps ) => {
	const opened = useDelayedOpening();
	useEffect( presenter.onMount, [] );

	return PRICING_PLANS_AVAILABLE && (
		<Modal
			opened={opened}
			onClose={presenter.close}
			title="Customize your plan"
			styles={MODAL_STYLES}
			size="468px"
		>
			<div className="AddOnsModal">
				{presenter.inStudioTrial && presenter.isSeatsSelected && (
					<Callout
						type="promotional"
						variant="outlined"
						text="Seats will keep access during your free trial and become paid when you upgrade."
						mt={8}
						mb={16}
					/>
				)}
				{!presenter.inStudioTrial && (
					<AddOnsSwitch
						onSwitch={presenter.addOnTypeWasSwitched}
						selectedAddOnType={presenter.selectedAddOnType}
					/>
				)}
				<Loader active={presenter.loading} />
				{!presenter.loading && (
					<Stack className="AddOnsModalContent">
						<Stack spacing="14px">
							<Title order={3}>{presenter.title}</Title>
							<Text color="dark.3" variant="body">{presenter.text}</Text>
							<PriceLine
								cost={presenter.cost}
								costDescription={presenter.costDescription}
								tooltipInfoText={presenter.infoText}
							/>
						</Stack>
						<Stack spacing="24px">
							{presenter.hasAddOns && (
								<Badge color="yellow" variant="filled" size="md" w="fit-content">
									<Text variant="body-bold">{presenter.quantityText}</Text>
								</Badge>
							)}
							{presenter.additionalInformation && (
								<Callout
									type="promotional"
									text={presenter.additionalInformation}
								/>
							)}
							<Button
								onClick={presenter.buyButtonWasClicked}
								fullWidth
								disabled={!presenter.actionsEnabled}
							>
								{presenter.buttonText}
							</Button>
							{presenter.hasAddOns && (
								<Button
									h={20}
									color="red"
									compact
									variant="white"
									onClick={presenter.removeButtonWasClicked}
									fullWidth
									disabled={!presenter.actionsEnabled}
								>
									{presenter.removeButtonText}
								</Button>
							)}
						</Stack>
					</Stack>
				)}
			</div>
		</Modal>
	);
};

export default observer( AddOnsModal as React.FC<AddOnsModalProps> );
